import React from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * StackDisclaimer — the AI/medical disclaimer that accompanies a rendered stack.
 *
 * The rule, as ruled by Tanu on 2026-09-14 (STACK-541): **there is never more
 * than one disclaimer on a surface**, and a surface carries one only if it is
 * the last place the reader sees that stack.
 *
 * That resolves to three cases:
 *
 *   - `SupplementsTable` (/create-stack results, /view-stack/:id,
 *     /edit-stack/:id, the curated stack pages, the medication + diet support
 *     views, and the dashboard's View modal) — the terminal surface for a
 *     stack, so it carries one.
 *   - A **My stacks card** on the dashboard carries none, and neither does the
 *     grid around it. The card is a summary that opens into View and Edit, and
 *     both of those carry the line through the table above, so the dashboard
 *     is not where that stack is read. (STACK-517 had moved the line from each
 *     cell to one under the grid, which stopped the sentence repeating but
 *     still said it on a screen that defers.)
 *   - The **Recommended band** carries one, inside its container and below both
 *     cards. A recommended card has no other surface — there is nothing to
 *     open — so the band is where those stacks are read. The 2026-09-14 ruling
 *     put the line on each card; on the preview that set the same sentence
 *     twice side by side, so Tanu re-ruled it on 2026-09-21 to one line for the
 *     pair, and the cards themselves carry none. A band whose cards are all
 *     locked or paused has no supplement row on screen and so shows none: a
 *     disclaimer for a stack the reader cannot see is the defect PR 326 fixed.
 *
 * Both `SupplementsTable` and `StackCard` take `hideDisclaimer` so a host can
 * suppress the line when it is not the terminal surface.
 *
 * The wording is legal copy, set by STACK-517 and amended by STACK-541. Do not
 * edit it for tone or length; changes go through the maintainer.
 */
export default function StackDisclaimer({ sx }) {
  return (
    <Box
      // The hook the one-disclaimer-per-surface rule is asserted with. Counting
      // rendered nodes is the only check that catches the failure that matters
      // here — a surface that quietly gains or loses the line — because every
      // host renders the same component and a text match cannot tell two of
      // them apart from one.
      data-testid="stack-disclaimer"
      sx={{
        mt: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        ...sx,
      }}
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: '1rem',
          mr: 0.75,
          color: 'text.secondary',
          mt: '0.0625rem',
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: '0.75rem',
          color: 'text.secondary',
          lineHeight: 1.4,
          fontWeight: 500,
        }}
      >
        AI can make mistakes. Review it with your healthcare provider before starting or changing any supplements or medications.
      </Typography>
    </Box>
  );
}
